import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { useEvidenceStore } from '../store/evidenceStore'
import SecureFileViewer from '../components/SecureFileViewer'
import { 
  BarChart3, 
  FileText, 
  Users, 
  Filter, 
  Trash2, 
  ExternalLink, 
  Calendar,
  Download,
  Settings
} from 'lucide-react'
import FormStructureManager from '../components/FormStructureManager'
import DownloadZipButton from '../components/DownloadZipButton'
import UserManagement from '../components/UserManagement'
import SupervisionMejoraContinua from '../components/admin/SupervisionMejoraContinua'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [filters, setFilters] = useState({
    programa: '',
    dimension: '',
    criterio: '',
    fechaCarga: '',
    año: ''
  })
  const [selectedEvidences, setSelectedEvidences] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [viewerFile, setViewerFile] = useState(null)
  const [showViewer, setShowViewer] = useState(false)
  
  const { token } = useAuthStore()
  const { 
    evidences, 
    formStructure, 
    fetchAllEvidences, 
    fetchFormStructure,
    deleteEvidences, 
    isLoading, 
    error 
  } = useEvidenceStore()

  useEffect(() => {
    fetchAllEvidences(token, filters)
    if (!formStructure) {
      fetchFormStructure()
    }
  }, [token, fetchAllEvidences, formStructure, fetchFormStructure])

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value }
    if (key === 'dimension') {
      newFilters.criterio = '' // Reset criterio when dimension changes
    }
    setFilters(newFilters)
  }

  const applyFilters = () => {
    fetchAllEvidences(token, filters)
  }

  const clearFilters = () => {
    const emptyFilters = {
      programa: '',
      dimension: '',
      criterio: '',
      fechaCarga: '',
      año: ''
    }
    setFilters(emptyFilters)
    fetchAllEvidences(token, emptyFilters)
  }

  const handleSelectEvidence = (evidenceId) => {
    setSelectedEvidences(prev => 
      prev.includes(evidenceId)
        ? prev.filter(id => id !== evidenceId)
        : [...prev, evidenceId]
    )
  }

  // Filtrar evidencias localmente
  const filteredEvidences = evidences.filter(evidence => {
    // Filtro por programa
    if (filters.programa && evidence.programa !== filters.programa) {
      return false
    }
    
    // Filtro por dimensión
    if (filters.dimension && evidence.dimension !== filters.dimension) {
      return false
    }
    
    // Filtro por criterio
    if (filters.criterio && evidence.criterio !== filters.criterio) {
      return false
    }
    
    // Filtro por año (solo usar el campo año explícito)
    if (filters.año) {
      if (!evidence.año || evidence.año.toString() !== filters.año.toString()) {
        return false
      }
    }
    
    // Filtro por fecha de carga
    if (filters.fechaCarga && evidence.fecha_hora) {
      const evidenceDate = new Date(evidence.fecha_hora).toISOString().split('T')[0]
      if (evidenceDate !== filters.fechaCarga) {
        return false
      }
    }
    
    return true
  })

  const handleSelectAll = () => {
    if (selectedEvidences.length === filteredEvidences.length) {
      setSelectedEvidences([])
    } else {
      setSelectedEvidences(filteredEvidences.map(e => e.id))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedEvidences.length === 0) return
    
    const result = await deleteEvidences(token, selectedEvidences)
    if (result.success) {
      setSelectedEvidences([])
      setShowDeleteModal(false)
    }
  }

  // Estadísticas (usando evidencias filtradas)
  const stats = {
    totalEvidences: filteredEvidences.length,
    totalPrograms: [...new Set(filteredEvidences.map(e => e.programa))].length,
    evidencesByDimension: filteredEvidences.reduce((acc, evidence) => {
      acc[evidence.dimension] = (acc[evidence.dimension] || 0) + 1
      return acc
    }, {}),
    evidencesByProgram: filteredEvidences.reduce((acc, evidence) => {
      acc[evidence.programa] = (acc[evidence.programa] || 0) + 1
      return acc
    }, {}),
    recentUploads: filteredEvidences.filter(e => {
      const uploadDate = new Date(e.fecha_hora)
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      return uploadDate > weekAgo
    }).length
  }

  const programs = [...new Set(evidences.map(e => e.programa))].sort()
  const criterios = filters.dimension && formStructure 
    ? (formStructure[filters.dimension] || []).map(c => 
        typeof c === 'object' ? c.nombre : c
      ) 
    : []

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'evidences', label: 'Todas las Evidencias', icon: FileText },
    { id: 'supervision', label: 'Supervisión Mejora Continua', icon: ExternalLink },
    { id: 'users', label: 'Gestión de Usuarios', icon: Users },
    { id: 'structure', label: 'Estructura del Formulario', icon: Settings },
    { id: 'management', label: 'Gestión de Archivos', icon: Trash2 },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Administrador</h1>
          <p className="text-gray-600">Gestión completa del sistema de evidencias</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Evidencias</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalEvidences}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Programas Activos</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalPrograms}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Subidas Recientes</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.recentUploads}</p>
                  <p className="text-xs text-gray-500">Última semana</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Dimensiones</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {Object.keys(stats.evidencesByDimension).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas por Programa */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📊 Evidencias por Programa
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Programa
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Esta Semana
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progreso
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(stats.evidencesByProgram || {})
                    .sort(([,a], [,b]) => b - a)
                    .map(([programa, count]) => {
                      const percentage = (count / stats.totalEvidences) * 100
                      const recentCount = evidences.filter(e => 
                        e.programa === programa && 
                        new Date(e.fecha_hora) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                      ).length
                      
                      return (
                        <tr key={programa} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-full">
                                <span className="text-blue-600 font-semibold text-sm">
                                  {programa.substring(0, 2).toUpperCase()}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{programa}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-lg font-semibold text-gray-900">{count}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              recentCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {recentCount > 0 ? `+${recentCount}` : '0'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 w-12 text-right">
                                {percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Grid de Dimensiones y Programas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Evidencias por Dimensión */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                📋 Distribución por Dimensión
              </h3>
              <div className="space-y-3">
                {Object.entries(stats.evidencesByDimension).map(([dimension, count]) => (
                  <div key={dimension} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 flex-1 pr-4">{dimension}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{ 
                            width: `${(count / stats.totalEvidences) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Criterios */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🎯 Top 5 Criterios
              </h3>
              <div className="space-y-3">
                {Object.entries(
                  evidences.reduce((acc, e) => {
                    acc[e.criterio] = (acc[e.criterio] || 0) + 1
                    return acc
                  }, {})
                )
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([criterio, count], index) => (
                    <div key={criterio} className="flex items-center space-x-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        index === 0 ? 'bg-yellow-100 text-yellow-600' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        <span className="text-sm font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{criterio}</p>
                      </div>
                      <span className="text-sm font-semibold text-gray-700">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Actividad Reciente por Programa */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📈 Actividad Reciente (Últimos 7 días)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(stats.evidencesByProgram || {})
                .map(([programa, totalCount]) => {
                  const recentCount = evidences.filter(e => 
                    e.programa === programa && 
                    new Date(e.fecha_hora) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                  ).length
                  const percentage = totalCount > 0 ? (recentCount / totalCount) * 100 : 0
                  
                  return (
                    <div key={programa} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{programa}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          recentCount > 0 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                        }`}>
                          {recentCount > 0 ? `+${recentCount}` : 'Sin actividad'}
                        </span>
                      </div>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-2xl font-bold text-blue-600">{recentCount}</span>
                        <span className="text-sm text-gray-600">de {totalCount} total</span>
                      </div>
                      <div className="mt-2 w-full bg-blue-200 rounded-full h-1.5">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}

      {/* Evidences Tab */}
      {activeTab === 'evidences' && (
        <div className="space-y-6">
          {/* Filtros */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <Filter className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Programa
                </label>
                <select
                  value={filters.programa}
                  onChange={(e) => handleFilterChange('programa', e.target.value)}
                  className="input-field"
                >
                  <option value="">Todos los programas</option>
                  {programs.map(programa => (
                    <option key={programa} value={programa}>{programa}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensión
                </label>
                <select
                  value={filters.dimension}
                  onChange={(e) => handleFilterChange('dimension', e.target.value)}
                  className="input-field"
                >
                  <option value="">Todas las dimensiones</option>
                  {formStructure && Object.keys(formStructure).map(dimension => (
                    <option key={dimension} value={dimension}>{dimension}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Criterio
                </label>
                <select
                  value={filters.criterio}
                  onChange={(e) => handleFilterChange('criterio', e.target.value)}
                  className="input-field"
                  disabled={!filters.dimension}
                >
                  <option value="">Todos los criterios</option>
                  {criterios.map(criterio => (
                    <option key={criterio} value={criterio}>{criterio}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de Carga
                </label>
                <input
                  type="date"
                  value={filters.fechaCarga}
                  onChange={(e) => handleFilterChange('fechaCarga', e.target.value)}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Año
                </label>
                <select
                  value={filters.año}
                  onChange={(e) => handleFilterChange('año', e.target.value)}
                  className="input-field"
                >
                  <option value="">Todos los años</option>
                  {Array.from({ length: 11 }, (_, i) => 2022 + i).map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-4">
              <button onClick={applyFilters} className="btn-primary">
                Aplicar Filtros
              </button>
              <button onClick={clearFilters} className="btn-secondary">
                Limpiar Filtros
              </button>

            </div>
          </div>

          {/* Lista de evidencias */}
          <div className="card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center justify-between sm:justify-start gap-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Evidencias ({filteredEvidences.length})
                </h3>
                
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedEvidences.length === filteredEvidences.length && filteredEvidences.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                  />
                  <span className="text-sm text-gray-700 whitespace-nowrap">Seleccionar todas</span>
                </label>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Botón de descarga ZIP */}
                <DownloadZipButton 
                  selectedEvidences={selectedEvidences}
                  allEvidences={filteredEvidences}
                  filters={filters}
                />

                {selectedEvidences.length > 0 && (
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="btn-danger flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Eliminar ({selectedEvidences.length})</span>
                  </button>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando evidencias...</p>
              </div>
            ) : filteredEvidences.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No se encontraron evidencias</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="w-12 px-4 py-3 text-center">
                        <span className="sr-only">Seleccionar</span>
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Archivo
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Programa
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dimensión
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Criterio
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Fecha
                      </th>
                      <th scope="col" className="w-32 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEvidences.map((evidence) => (
                      <tr key={evidence.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedEvidences.includes(evidence.id)}
                            onChange={() => handleSelectEvidence(evidence.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center min-w-0">
                            <FileText className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-900 truncate max-w-xs" title={evidence.nombre_archivo}>
                              {evidence.nombre_archivo}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {evidence.programa}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={evidence.dimension}>
                            {evidence.dimension}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={evidence.criterio}>
                            {evidence.criterio}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {format(new Date(evidence.fecha_hora), 'dd/MM/yyyy', { locale: es })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {format(new Date(evidence.fecha_hora), 'HH:mm', { locale: es })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center space-x-1">
                            {evidence.url_archivo ? (
                              <button
                                onClick={() => {
                                  setViewerFile({
                                    url: evidence.url_archivo,
                                    name: evidence.nombre_archivo
                                  })
                                  setShowViewer(true)
                                }}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                title="Descargar"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            ) : (
                              <span className="p-2 text-gray-400" title="Sin archivo">
                                <FileText className="h-4 w-4" />
                              </span>
                            )}
                            <button
                              onClick={() => {
                                setSelectedEvidences([evidence.id])
                                setShowDeleteModal(true)
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Supervision Tab */}
      {activeTab === 'supervision' && (
        <SupervisionMejoraContinua />
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <UserManagement />
      )}

      {/* Structure Tab */}
      {activeTab === 'structure' && (
        <FormStructureManager />
      )}

      {/* Management Tab */}
      {activeTab === 'management' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Gestión de Archivos - Modo Administrador
          </h3>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Zona de Eliminación Avanzada
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Esta sección permite eliminar cualquier archivo del sistema. 
                    Use con extrema precaución ya que las eliminaciones son permanentes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-gray-600 mb-4">
            Utilice las pestañas anteriores para seleccionar y eliminar archivos. 
            Todas las eliminaciones requieren confirmación múltiple para evitar errores.
          </p>
          
          <div className="flex space-x-3">
            <button
              onClick={() => setActiveTab('evidences')}
              className="btn-primary"
            >
              Ir a Gestión de Evidencias
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmar Eliminación
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar {selectedEvidences.length} evidencia(s)? 
              Esta acción no se puede deshacer y eliminará permanentemente los archivos del sistema.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary flex-1"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={isLoading}
                className="btn-danger flex-1 disabled:opacity-50"
              >
                {isLoading ? 'Eliminando...' : 'Eliminar Permanentemente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Visualizador de archivos */}
      <SecureFileViewer
        fileName={viewerFile}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false)
          setViewerFile(null)
        }}
      />
    </div>
  )
}

export default AdminDashboard